import React, { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { colors } from "../theme";
import { View, Button, ScreenHeader, EmptyState } from "../ui";
import { QuestionRunner, ResultSummary } from "../QuestionRunner";
import { useI18n } from "../i18n";
import { useAppState } from "../state";
import { ALL_QUESTIONS, CASES, SPOTS, examById, pickQuestions, questionById, t } from "../content";

export default function Session() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { tr, lang } = useI18n();
  const st = useAppState();
  const [phase, setPhase] = useState("run");
  const [result, setResult] = useState({ c: 0, t: 0 });

  const mode = params.get("mode");
  const examId = params.get("examId");
  const area = params.get("area");
  const exam = examId ? examById(examId) : undefined;

  const { questions, title } = useMemo(() => {
    let qs = [];
    let title = tr("quickQuiz");
    switch (mode) {
      case "exam":
        if (exam) { qs = pickQuestions(exam.count, { area: exam.areaFilter }); title = t(exam.title, lang); }
        break;
      case "quick": qs = pickQuestions(10); title = tr("quickTest"); break;
      case "daily": {
        const weak = st.weakAreas()[0]?.areaId;
        qs = pickQuestions(8, { priorityIds: st.state.mistakes });
        if (weak && qs.length < 8) qs = pickQuestions(8, { area: weak });
        title = tr("dailyReview");
        break;
      }
      case "cases": qs = [...CASES]; title = tr("realCases"); break;
      case "spot": qs = [...SPOTS]; title = tr("spotMistake"); break;
      case "truefalse": qs = ALL_QUESTIONS.filter((q) => q.type === "truefalse").slice(0, 12); title = tr("trueFalse"); break;
      case "mistakes": qs = st.state.mistakes.map((id) => questionById(id)).filter(Boolean); title = tr("myMistakes"); break;
      case "area": qs = pickQuestions(12, { area }); title = tr("practiceQuiz"); break;
      default: qs = pickQuestions(10);
    }
    return { questions: qs, title };
  }, [mode, examId, area, lang]); // eslint-disable-line

  if (!questions || questions.length === 0) {
    return (
      <View style={{ flex: 1, minHeight: "100vh", backgroundColor: colors.surface }}>
        <ScreenHeader title={title} onBack={() => navigate(-1)} />
        <EmptyState icon="confetti" title={mode === "mistakes" ? tr("noMistakes") : tr("noCardsDue")} />
        <View style={{ padding: 20 }}><Button title={tr("back")} variant="secondary" onPress={() => navigate(-1)} /></View>
      </View>
    );
  }

  if (phase === "done") {
    return (
      <View style={{ flex: 1, minHeight: "100vh", backgroundColor: colors.surface }}>
        <ScreenHeader title={title} onBack={() => navigate(-1)} />
        <ResultSummary
          correct={result.c} total={result.t}
          onDone={() => navigate(-1)}
          onRetry={() => { setResult({ c: 0, t: 0 }); setPhase("run"); }}
        />
        {exam && (
          <View style={{ padding: "0 20px" }}>
            <Button testID="review-exam" title={tr("reviewAnswers")} variant="secondary" icon="eye" onPress={() => navigate("/exam-review/0")} />
          </View>
        )}
      </View>
    );
  }

  return (
    <QuestionRunner
      title={title}
      questions={questions}
      onExit={() => navigate(-1)}
      onFinish={(c, tot, answers) => {
        if (exam) st.addExamResult(exam.id, t(exam.title, lang), c, tot, answers);
        if (mode === "cases") CASES.forEach((q) => st.markDone("cases", q.id));
        if (mode === "spot") SPOTS.forEach((q) => st.markDone("spots", q.id));
        setResult({ c, t: tot });
        setPhase("done");
      }}
    />
  );
}
